/**
 * Fansly's creator Stats (fansly.com/creator/stats/*): the period window,
 * metric deltas and the payload readers, ported from Fansly's own stats
 * service (bundle chunk 729 + main) so the numbers match what the creator
 * sees on Fansly. Money is thousandths ("mills"), buckets are UTC day starts.
 */
import { asList, asObj, num, str, type Raw } from '../data'

export const DAY_MS = 864e5
export const HOUR_MS = 36e5

// ── Period window (Fansly's trackingLinkService.buildGrowthWindow) ─────────

export interface StatsWindow {
  days: number
  /** First and last UTC day bucket of the period (both included). */
  currentStart: number
  currentEnd: number
  /** The same number of days right before. */
  previousStart: number
  previousEnd: number
  splitAt: number
  /** Tracking-link stats span both periods: rows in (spanAfter, spanBefore). */
  spanBefore: number
  spanAfter: number
}

export type PeriodKey = '7' | '30' | '90' | 'custom'
export const PERIODS: readonly PeriodKey[] = ['7', '30', '90']

export interface Period {
  key: PeriodKey
  /** Custom: YYYY-MM-DD days (the operator's local calendar days). */
  from?: string
  to?: string
}

export function todayBucket(now = Date.now()): number {
  const d = new Date(now)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

export function buildWindow(start: number, end: number): StatsWindow {
  const days = Math.round((end - start) / DAY_MS) + 1
  const previousEnd = start - DAY_MS
  const before = previousEnd - days * DAY_MS
  return {
    days,
    currentStart: start,
    currentEnd: end,
    previousStart: before + DAY_MS,
    previousEnd,
    splitAt: previousEnd,
    spanBefore: end + DAY_MS,
    spanAfter: before,
  }
}

/** The last `days` days, today included. */
export function windowForDays(days: number, now = Date.now()): StatsWindow {
  const end = todayBucket(now)
  return buildWindow(end - (days - 1) * DAY_MS, end)
}

/** "2026-10-01" → its UTC bucket (Fansly maps the local calendar day to the same UTC date). */
export function dayBucket(day: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN
}

export function utcDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

/** A custom range of days: ordered, never past today. */
export function windowForRange(from: string, to: string, now = Date.now()): StatsWindow {
  let a = dayBucket(from)
  let b = dayBucket(to)
  const today = todayBucket(now)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return windowForDays(30, now)
  if (b < a) [a, b] = [b, a]
  if (b > today) b = today
  if (a > b) a = b
  return buildWindow(a, b)
}

export function windowOf(period: Period, now = Date.now()): StatsWindow {
  if (period.key === 'custom' && period.from && period.to) return windowForRange(period.from, period.to, now)
  return windowForDays(Number(period.key === 'custom' ? 30 : period.key), now)
}

/** The query every period-scoped Stats route takes (epoch ms). */
export function windowQuery(w: StatsWindow): { after: number; before: number } {
  return { after: w.currentStart, before: w.currentEnd }
}

/** Fansly's rule: more than 400 days charts by month, else by day. */
export function granularityFor(w: StatsWindow): 'day' | 'month' {
  return w.days > 400 ? 'month' : 'day'
}

// ── Metrics ────────────────────────────────────────────────────────────────

/** `{value, previous, delta, deltaPercent}` → the two numbers that matter. */
export interface Metric {
  value: number
  previous: number
  /** The period before predates Fansly's data: there is nothing to compare with. */
  noPrevious?: boolean
}

export const EMPTY_METRIC: Metric = { value: 0, previous: 0 }

export function metricOf(v: unknown): Metric {
  const o = asObj(v)
  if (!('value' in o) && !('previous' in o)) return EMPTY_METRIC
  return { value: num(o.value), previous: num(o.previous) }
}

/** Percent change against the period before; null when there is nothing to compare with. */
export function changePct(m: Metric): number | null {
  if (m.noPrevious || !m.previous) return null
  return ((m.value - m.previous) / Math.abs(m.previous)) * 100
}

/** a / b × scale for both periods (Fansly's ratioMetric: refund rate, avg per fan…). */
export function ratioMetric(a: Metric, b: Metric, scale = 1): Metric {
  return {
    value: b.value ? (a.value / b.value) * scale : 0,
    previous: b.previous ? (a.previous / b.previous) * scale : 0,
  }
}

export function minusMetric(a: Metric, b: Metric): Metric {
  return { value: a.value - b.value, previous: a.previous - b.previous }
}

/**
 * When Fansly started keeping the daily follower and subscriber counts of the
 * account (`dataSince`, 2026-09-20 for every account); 0 when unknown.
 */
export function levelsSince(summary: unknown): number {
  return num(asObj(summary).dataSince)
}

/**
 * A follower / subscriber count is the level at the end of each period, so
 * the period before has one only when it ends on or after `dataSince`;
 * otherwise Fansly answers previous = 0, which is "no data", not "new".
 */
export function levelsComparable(summary: unknown): boolean {
  const since = levelsSince(summary)
  return !since || num(asObj(summary).previousBeforeBucket) >= since
}

/** A level metric (followerCount, subscriberCount) of the summary, marked when it can't be compared. */
export function levelAt(summary: unknown, key: string): Metric {
  const m = metricAt(summary, key)
  return levelsComparable(summary) ? m : { ...m, noPrevious: true }
}

/** Walks `o.a.b…` and reads the metric there. */
export function metricAt(o: unknown, ...path: string[]): Metric {
  let cur: unknown = o
  for (const k of path) cur = asObj(cur)[k]
  return metricOf(cur)
}

// ── Gross / Net ────────────────────────────────────────────────────────────

/**
 * The page-wide money choice: Gross is what fans paid, Net what the creator
 * keeps after Fansly's fee. Every money figure follows it; the headline
 * Earnings also names the other one.
 */
export type MoneyMode = 'gross' | 'net'
export const MONEY_MODES: readonly MoneyMode[] = ['gross', 'net']

export function otherMode(mode: MoneyMode): MoneyMode {
  return mode === 'gross' ? 'net' : 'gross'
}

/** The revenue fields of one mode: the amount before refunds and the refunded part. */
export function moneyFields(mode: MoneyMode): { amount: 'grossMills' | 'netMills'; refunded: 'refundedGrossMills' | 'refundedNetMills' } {
  return mode === 'gross' ? { amount: 'grossMills', refunded: 'refundedGrossMills' } : { amount: 'netMills', refunded: 'refundedNetMills' }
}

/** Earnings of the period in a mode: the amount after refunds (Fansly's Earnings is the net one). */
export function earnedMetric(summary: unknown, mode: MoneyMode): Metric {
  const f = moneyFields(mode)
  return minusMetric(metricAt(summary, 'revenue', f.amount), metricAt(summary, 'revenue', f.refunded))
}

// ── Labels Fansly uses ─────────────────────────────────────────────────────

/** Content surfaces (Fansly's source enum); the Content switch offers For You, Timeline and Other. */
export const SOURCES = { fyp: 0, timeline: 1, suggestions: 2, search: 3, other: 4 } as const
export const CONTENT_SOURCES = [SOURCES.fyp, SOURCES.timeline, SOURCES.other] as const
export const SOURCE_KEYS: Record<number, string> = { 0: 'fyp', 1: 'timeline', 2: 'suggestions', 3: 'search', 4: 'other' }
/** Profile visits name source 1 "Direct" instead of "Timeline". */
export const PROFILE_SOURCE_KEYS: Record<number, string> = { 0: 'fyp', 1: 'direct', 2: 'suggestions', 3: 'search', 4: 'other' }

/** Product types of earnings (Fansly's productTypeLabel); anything else is "other". */
export const PRODUCT_TYPES: Record<string, string> = {
  '7001': 'tipsLegacy',
  '7101': 'tips',
  '2016': 'mediaSetsLegacy',
  '2010': 'mediaLegacy',
  '2116': 'mediaSets',
  '2110': 'media',
  '15001': 'subscriptions',
  '18001': 'referrals',
  '18002': 'referrals',
  '45001': 'streamTickets',
  '45101': 'streamTickets',
  '32001': 'lockedText',
  '32101': 'lockedText',
  '24101': 'leaderboard',
  '6101': 'refunds',
}

export function productKey(type: unknown): string {
  return PRODUCT_TYPES[str(type)] ?? 'other'
}

/** Top media sort (Fansly's media orderBy). */
export const MEDIA_ORDERS = ['views', 'uniqueViewers', 'watchMs', 'completedViews'] as const
export type MediaOrder = (typeof MEDIA_ORDERS)[number]
/** Top fans sort: by money (in the page's mode) or by purchases. */
export const FAN_ORDERS = ['money', 'transactions'] as const
export type FanOrder = (typeof FAN_ORDERS)[number]

/** Fansly's fans orderBy for a sort in a mode. */
export function fanOrderBy(order: FanOrder, mode: MoneyMode): 'netMills' | 'grossMills' | 'transactions' {
  return order === 'transactions' ? 'transactions' : moneyFields(mode).amount
}

// ── Series ─────────────────────────────────────────────────────────────────

export interface Bucketed {
  bucket: number
  value: number
}

/** `[{bucket, value}]` (summary series) sorted by bucket. */
export function bucketList(v: unknown): Bucketed[] {
  return asList(v)
    .map((r) => ({ bucket: num(r.bucket), value: num(r.value) }))
    .filter((r) => r.bucket > 0)
    .sort((a, b) => a.bucket - b.bucket)
}

/** Every day bucket from `from` to `to` (or month starts). */
export function bucketsBetween(from: number, to: number, granularity: 'day' | 'month' = 'day'): number[] {
  const out: number[] = []
  if (!(from > 0) || !(to >= from)) return out
  if (granularity === 'month') {
    const d = new Date(from)
    let m = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)
    while (m <= to) {
      out.push(m)
      const n = new Date(m)
      m = Date.UTC(n.getUTCFullYear(), n.getUTCMonth() + 1, 1)
    }
    return out
  }
  for (let b = from; b <= to; b += DAY_MS) out.push(b)
  return out
}

/** Points for the chart (the OF StatsChart reads `{date, count}`), zero-filled over the window. */
export function chartPoints(list: Bucketed[], w: StatsWindow, scale = 1, granularity: 'day' | 'month' = 'day'): { date: string; count: number }[] {
  const byBucket = new Map<number, number>()
  for (const p of list) {
    const key = granularity === 'month' ? monthStartOf(p.bucket) : p.bucket
    byBucket.set(key, (byBucket.get(key) ?? 0) + p.value)
  }
  const buckets = bucketsBetween(granularity === 'month' ? monthStartOf(w.currentStart) : w.currentStart, w.currentEnd, granularity)
  return buckets.map((b) => ({ date: new Date(b).toISOString(), count: (byBucket.get(b) ?? 0) * scale }))
}

export function monthStartOf(ms: number): number {
  const d = new Date(ms)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)
}

/** The bucket of a series row: `bucketField` names it (`hourBucket` for hourly series), else `bucket`. */
export function rowBucket(row: Raw, field = ''): number {
  return num(field ? row[field] : undefined) || num(row.bucket) || num(row.hourBucket) || num(row.dayBucket) || num(row.monthBucket)
}

export interface RevenueBucket {
  bucket: number
  transactions: number
  grossMills: number
  netMills: number
  refundedGrossMills: number
  refundedNetMills: number
  /** Net after refunds (what Fansly calls Earnings). */
  earnedMills: number
  /** Gross after refunds. */
  earnedGrossMills: number
}

/** A bucket's earnings (after refunds) in a mode. */
export function earnedIn(b: RevenueBucket, mode: MoneyMode): number {
  return mode === 'gross' ? b.earnedGrossMills : b.earnedMills
}

/** Revenue series rows (one per product type and bucket) summed per bucket. */
export function revenueByBucket(payload: unknown, granularity: 'day' | 'month' | 'hour' = 'day'): RevenueBucket[] {
  const o = asObj(payload)
  const field = str(o.bucketField)
  const map = new Map<number, RevenueBucket>()
  for (const r of asList(o.rows)) {
    let b = rowBucket(r, field)
    if (!b) continue
    if (granularity === 'month') b = monthStartOf(b)
    let e = map.get(b)
    if (!e) {
      e = { bucket: b, transactions: 0, grossMills: 0, netMills: 0, refundedGrossMills: 0, refundedNetMills: 0, earnedMills: 0, earnedGrossMills: 0 }
      map.set(b, e)
    }
    e.transactions += num(r.transactions)
    e.grossMills += num(r.grossMills)
    e.netMills += num(r.netMills)
    e.refundedGrossMills += num(r.refundedGrossMills)
    e.refundedNetMills += num(r.refundedNetMills)
    e.earnedMills = e.netMills - e.refundedNetMills
    e.earnedGrossMills = e.grossMills - e.refundedGrossMills
  }
  return [...map.values()].sort((a, b) => a.bucket - b.bucket)
}

/** Views series rows of one surface (`source`), summed per bucket. */
export function viewsByBucket(payload: unknown, source: number | null, field: 'views' | 'imageViews' | 'watchMs' = 'views'): Bucketed[] {
  const o = asObj(payload)
  const bf = str(o.bucketField)
  const map = new Map<number, number>()
  for (const r of asList(o.rows)) {
    if (source !== null && num(r.source) !== source) continue
    const b = rowBucket(r, bf)
    if (!b) continue
    map.set(b, (map.get(b) ?? 0) + num(r[field]))
  }
  return [...map.entries()].map(([bucket, value]) => ({ bucket, value })).sort((a, b) => a.bucket - b.bucket)
}

// ── Glance: today / yesterday on the operator's calendar ───────────────────

export interface Glance {
  today: Metric
  yesterday: Metric
}

/**
 * Earnings today and yesterday (local days) from an hourly revenue series of
 * the last three days, each compared with the same hours of the day before
 * (Fansly's overview glance).
 */
export function glanceOf(payload: unknown, now = Date.now(), mode: MoneyMode = 'net'): Glance {
  const d = new Date(now)
  const startToday = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const startYesterday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1).getTime()
  const startBefore = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 2).getTime()
  const sameHourYesterday = Math.floor(now / HOUR_MS) * HOUR_MS - DAY_MS
  let today = 0
  let yesterday = 0
  let yesterdaySoFar = 0
  let dayBefore = 0
  for (const b of revenueByBucket(payload, 'hour')) {
    const earned = earnedIn(b, mode)
    if (b.bucket >= startToday) today += earned
    else if (b.bucket >= startYesterday) {
      yesterday += earned
      if (b.bucket <= sameHourYesterday) yesterdaySoFar += earned
    } else if (b.bucket >= startBefore) dayBefore += earned
  }
  return { today: { value: today, previous: yesterdaySoFar }, yesterday: { value: yesterday, previous: dayBefore } }
}

// ── Summary ────────────────────────────────────────────────────────────────

/** The summary's block for one surface (`views[]` has one row per source). */
export function viewsOf(summary: unknown, source: number): Raw {
  return asList(asObj(summary).views).find((r) => num(r.source) === source) ?? {}
}

export interface Level {
  bucket: number
  followers: number
  subscribers: number
}

export function levelsOf(summary: unknown): Level[] {
  return asList(asObj(summary).levels)
    .map((r) => ({ bucket: num(r.bucket), followers: num(r.followerCount), subscribers: num(r.subscriberCount) }))
    .filter((r) => r.bucket > 0)
    .sort((a, b) => a.bucket - b.bucket)
}

export interface TierRow {
  id: string
  name: string
  color: string
  active: boolean
  pos: number
  subscribers: Metric
  added: Metric
  renewed: Metric
  expired: Metric
  cancelled: Metric
  movedIn: Metric
  movedOut: Metric
}

export function tiersOf(summary: unknown): TierRow[] {
  const comparable = levelsComparable(summary)
  return asList(asObj(asObj(summary).subscriptions).byTier)
    .map((r) => ({
      id: str(r.tierId),
      name: str(r.tierName),
      color: /^#[0-9a-f]{3,8}$/i.test(str(r.tierColor)) ? str(r.tierColor) : '',
      active: r.tierActive !== false,
      pos: num(r.tierPos),
      subscribers: comparable ? metricOf(r.subscriberCount) : { ...metricOf(r.subscriberCount), noPrevious: true },
      added: metricOf(r.subscriptionsNew),
      renewed: metricOf(r.subscriptionsRenewed),
      expired: metricOf(r.subscriptionsExpired),
      cancelled: metricOf(r.subscriptionsCancelled),
      movedIn: metricOf(r.subscriptionsMovedIn),
      movedOut: metricOf(r.subscriptionsMovedOut),
    }))
    .sort((a, b) => Number(b.active) - Number(a.active) || a.pos - b.pos)
}

export interface ProductRow {
  type: string
  key: string
  transactions: Metric
  grossMills: Metric
  netMills: Metric
  refunds: Metric
  refundedGrossMills: Metric
  refundedNetMills: Metric
}

export function productsOf(summary: unknown, mode: MoneyMode = 'net'): ProductRow[] {
  const f = moneyFields(mode)
  return asList(asObj(asObj(summary).revenue).byProductType)
    .map((r) => ({
      type: str(r.productType),
      key: productKey(r.productType),
      transactions: metricOf(r.transactions),
      grossMills: metricOf(r.grossMills),
      netMills: metricOf(r.netMills),
      refunds: metricOf(r.refunds),
      refundedGrossMills: metricOf(r.refundedGrossMills),
      refundedNetMills: metricOf(r.refundedNetMills),
    }))
    .sort((a, b) => b[f.amount].value - a[f.amount].value)
}

export interface ProfileSourceRow {
  source: number
  visits: Metric
  unique: Metric
  watchMs: Metric
}

export function profileSourcesOf(summary: unknown): ProfileSourceRow[] {
  return asList(asObj(asObj(summary).profile).bySource)
    .map((r) => ({ source: num(r.source), visits: metricOf(r.profileVisits), unique: metricOf(r.uniqueVisitors), watchMs: metricOf(r.profileWatchMs) }))
    .sort((a, b) => b.visits.value - a.visits.value)
}

// ── Top media ──────────────────────────────────────────────────────────────

export interface TopMediaRow {
  offerId: string
  /** accountMedia id (the offer) used to find the thumbnail in aggregationData.accountMedia. */
  bestMediaId: string
  mediaType: number
  durationMs: number
  views: number
  impressions: number
  uniqueViewers: number
  watchMs: number
  videoViews: number
  completedViews: number
  watchPctSum: number
  replays: number
  likes: number
}

/** One row per media offer: its best media's numbers (Fansly ranks offers by their best media). */
export function topMediaOf(payload: unknown): TopMediaRow[] {
  return asList(asObj(payload).offers).map((o) => {
    const media = asList(o.media)
    const best = media.find((m) => str(m.mediaId) === str(o.bestMediaId)) ?? media[0] ?? {}
    return {
      offerId: str(o.mediaOfferId),
      bestMediaId: str(o.bestMediaId) || str(best.mediaId),
      mediaType: num(best.mediaType),
      durationMs: num(best.durationMs),
      views: num(best.views),
      impressions: num(best.impressions),
      uniqueViewers: num(best.uniqueViewers),
      watchMs: num(best.watchMs),
      videoViews: num(best.videoViews),
      completedViews: num(best.completedViews),
      watchPctSum: num(best.watchPctSum),
      replays: num(best.replays),
      likes: Math.max(0, num(o.likes) - num(o.unlikes)),
    }
  })
}

/** Average watched %: watchPctSum is in hundredths of a percent per video view. */
export function watchedPercent(watchPctSum: number, videoViews: number): number | null {
  return videoViews ? Math.round(watchPctSum / videoViews / 100) : null
}

/**
 * The accountMedia behind an offer: an offer id is the accountMedia id; the
 * best media is either its file or its preview.
 */
export function offerMedia(accountMedia: Map<string, Raw>, row: TopMediaRow): { accountMedia: Raw | null; usePreview: boolean } {
  const am = accountMedia.get(row.offerId) ?? [...accountMedia.values()].find((x) => str(asObj(x.media).id) === row.bestMediaId || str(asObj(x.preview).id) === row.bestMediaId) ?? null
  if (!am) return { accountMedia: null, usePreview: false }
  return { accountMedia: am, usePreview: str(asObj(am.preview).id) === row.bestMediaId && str(asObj(am.media).id) !== row.bestMediaId }
}

// ── Benchmarks, geo, tags, active hours ────────────────────────────────────

/** One video length bucket: `avgWatchPercent` is in percent, `completionRate` a fraction. */
export interface LengthBucket {
  minMs: number
  maxMs: number
  mediaCount: number
  views: number
  avgWatchMs: number
  avgWatchPercent: number
  completionRate: number
}

export function benchmarksOf(payload: unknown): LengthBucket[] {
  return asList(asObj(payload).buckets)
    .map((b) => ({
      minMs: num(b.minMs),
      maxMs: num(b.maxMs),
      mediaCount: num(b.mediaCount),
      views: num(b.videoViews) || num(b.views),
      avgWatchMs: num(b.avgWatchMs),
      avgWatchPercent: num(b.avgWatchPercent),
      completionRate: num(b.completionRate),
    }))
    .sort((a, b) => a.minMs - b.minMs)
}

/** "Up to 15s" / "15 to 30s" / "Over 300s" (Fansly's lengthBucketLabel) as i18n parts. */
export function lengthBucketParts(b: LengthBucket): { key: 'upTo' | 'between' | 'over'; min: number; max: number } {
  const min = Math.round(b.minMs / 1000)
  const max = Math.round(b.maxMs / 1000)
  if (!b.maxMs) return { key: 'over', min, max }
  if (!b.minMs) return { key: 'upTo', min, max }
  return { key: 'between', min, max }
}

export interface GeoRow {
  country: string
  views: number
  watchMs: number
  avgWatchPercent: number
}

export function geoOf(payload: unknown): { rows: GeoRow[]; profile: { country: string; visits: number }[] } {
  const o = asObj(payload)
  return {
    rows: asList(o.rows)
      .map((r) => ({ country: str(r.country), views: num(r.views) + num(r.imageViews), watchMs: num(r.watchMs), avgWatchPercent: num(r.avgWatchPercent) }))
      .sort((a, b) => b.views - a.views),
    profile: asList(o.profileRows)
      .map((r) => ({ country: str(r.country), visits: num(r.profileVisits) }))
      .sort((a, b) => b.visits - a.visits),
  }
}

/** "DE" → "Germany" in the operator's language. */
export function countryName(code: string, lang: string): string {
  if (!code) return ''
  try {
    return new Intl.DisplayNames([lang || 'en'], { type: 'region' }).of(code.toUpperCase()) || code
  } catch {
    return code
  }
}

export interface TagRow {
  id: string
  tag: string
  views: number
  watchMs: number
  liftPoints: number | null
}

export function tagsOf(payload: unknown): TagRow[] {
  const o = asObj(payload)
  const agg = asObj(o.aggregationData)
  const names = new Map(asList(agg.tags).map((t) => [str(t.id), str(t.tag) || str(t.label)]))
  const lift = o.liftAvailable === true
  return asList(o.rows).map((r) => ({
    id: str(r.tagId),
    tag: names.get(str(r.tagId)) ?? '',
    views: num(r.views) + num(r.imageViews),
    watchMs: num(r.watchMs) + num(r.imageWatchMs),
    liftPoints: lift && num(r.liftVideoViews) >= 50 ? num(r.liftPoints) : null,
  }))
}

/** Weekdays as Fansly shows them: Monday first (rows of `views[day][hour]`, day 0 = Sunday). */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const

export interface Heat {
  rows: { day: number; cells: number[] }[]
  max: number
  peak: { day: number; hour: number; value: number } | null
}

export function heatOf(matrix: unknown): Heat {
  const m = Array.isArray(matrix) ? (matrix as unknown[]) : []
  let max = 0
  let peak: Heat['peak'] = null
  const rows = WEEK_ORDER.map((day) => {
    const src = Array.isArray(m[day]) ? (m[day] as unknown[]) : []
    const cells = Array.from({ length: 24 }, (_, h) => num(src[h]))
    cells.forEach((v, hour) => {
      if (v > max) max = v
      if (v > 0 && (!peak || v > peak.value)) peak = { day, hour, value: v }
    })
    return { day, cells }
  })
  return { rows, max, peak }
}

// ── Top fans ───────────────────────────────────────────────────────────────

export interface FanRow {
  fanId: string
  transactions: number
  grossMills: number
  netMills: number
  refunds: number
  netAfterRefundsMills: number
  grossAfterRefundsMills: number
  firstBucket: number
}

/** What a fan earned the account in the period, after refunds, in a mode. */
export function fanEarned(r: FanRow, mode: MoneyMode): number {
  return mode === 'gross' ? r.grossAfterRefundsMills : r.netAfterRefundsMills
}

export function fansOf(payload: unknown): FanRow[] {
  return asList(asObj(payload).rows).map((r) => ({
    fanId: str(r.fanId),
    transactions: num(r.transactions),
    grossMills: num(r.grossMills),
    netMills: num(r.netMills),
    refunds: num(r.refunds),
    netAfterRefundsMills: 'netAfterRefundsMills' in r ? num(r.netAfterRefundsMills) : num(r.netMills) - num(r.refundedNetMills),
    grossAfterRefundsMills: num(r.grossMills) - num(r.refundedGrossMills),
    firstBucket: num(r.firstBucket),
  }))
}

/** A fan's earnings in the period per product type (earnings/account-stats rows). */
export function fanBreakdown(payload: unknown): { key: string; grossMills: number; netMills: number }[] {
  const map = new Map<string, { key: string; grossMills: number; netMills: number }>()
  for (const r of asList(payload)) {
    const key = productKey(r.type)
    const e = map.get(key) ?? { key, grossMills: 0, netMills: 0 }
    e.grossMills += num(r.totalGross)
    e.netMills += num(r.totalNet)
    map.set(key, e)
  }
  return [...map.values()].sort((a, b) => b.netMills - a.netMills)
}

// ── Formatting ─────────────────────────────────────────────────────────────

/** 75000 ms → "1m 15s"; "1h 05m" past an hour (Fansly's formatDuration). */
export function formatMs(ms: number): string {
  const s = Math.round((ms || 0) / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const r = s % 60
  if (m < 60) return `${m}m ${String(r).padStart(2, '0')}s`
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`
}

export function formatCount(n: number, lang = 'en'): string {
  try {
    return new Intl.NumberFormat(lang, { maximumFractionDigits: 1, notation: Math.abs(n) >= 100000 ? 'compact' : 'standard' }).format(n)
  } catch {
    return String(Math.round(n))
  }
}

export function formatMills(mills: number, lang = 'en'): string {
  try {
    return new Intl.NumberFormat(lang, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(mills / 1000)
  } catch {
    return `$${(mills / 1000).toFixed(2)}`
  }
}

export function formatPct(n: number, lang = 'en'): string {
  try {
    return `${new Intl.NumberFormat(lang, { maximumFractionDigits: 1 }).format(n)}%`
  } catch {
    return `${n.toFixed(1)}%`
  }
}

export type Format = 'count' | 'money' | 'duration' | 'percent'

/** A number as a tile shows it: money is mills, durations are ms. */
export function formatValue(v: number, format: Format, lang: string): string {
  if (format === 'money') return formatMills(v, lang)
  if (format === 'duration') return formatMs(v)
  if (format === 'percent') return formatPct(v, lang)
  return formatCount(v, lang)
}

/** "1 Oct – 30 Oct 2026" for the period bar (UTC days). */
export function rangeLabel(w: StatsWindow, lang = 'en'): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', timeZone: 'UTC' }
  const a = new Date(w.currentStart)
  const b = new Date(w.currentEnd)
  const year = (d: Date) => d.toLocaleDateString(lang, { year: 'numeric', timeZone: 'UTC' })
  const left = a.toLocaleDateString(lang, a.getUTCFullYear() === b.getUTCFullYear() ? opts : { ...opts, year: 'numeric' })
  return `${left} – ${b.toLocaleDateString(lang, opts)} ${year(b)}`
}
